/**
 * lib-blueprint
 *
 * Adapptio library for processing blueprints.
 *
 * @package lib-blueprint
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { OBJECT_TYPE, OBJECT_TYPE_PROP_NAME } from "../constants";

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
export function dataEqual(a: any, b: any) {
	// Direct match
	if (a === b) {
		return true;
	}

	// Is function
	if (a && b && typeof a === "function" && typeof b === "function") {
		return true;
	}

	// Is object
	if (a && b && typeof a === "object" && typeof b === "object") {
		if (
			a[OBJECT_TYPE_PROP_NAME] === OBJECT_TYPE.SCOPE &&
			b[OBJECT_TYPE_PROP_NAME] === OBJECT_TYPE.SCOPE
		) {
			return true;
		}

		if (a.constructor !== b.constructor) return false;

		// Check array
		if (Array.isArray(a)) {
			const length = a.length;

			if (length != b.length) {
				return false;
			}

			for (let i = length; i-- !== 0; ) {
				if (!dataEqual(a[i], b[i])) return false;
			}

			return true;
		}

		// Check map
		if (a instanceof Map && b instanceof Map) {
			if (a.size !== b.size) {
				return false;
			}

			for (const i of a.entries()) {
				if (!b.has(i[0])) {
					return false;
				}
			}

			for (const i of a.entries()) {
				if (!dataEqual(i[1], b.get(i[0]))) {
					return false;
				}
			}

			return true;
		}

		// Check set
		if (a instanceof Set && b instanceof Set) {
			if (a.size !== b.size) {
				return false;
			}

			for (const i of a.entries()) {
				if (!b.has(i[0])) return false;
			}

			return true;
		}

		// Check array buffer
		if (ArrayBuffer.isView(a) && ArrayBuffer.isView(b)) {
			const length = a.byteLength;

			if (length != b.byteLength) {
				return false;
			}

			for (let i = length; i-- !== 0; ) {
				if (a[i] !== b[i]) return false;
			}

			return true;
		}

		if (a.constructor === RegExp) return a.source === b.source && a.flags === b.flags;
		if (a.valueOf !== Object.prototype.valueOf) return a.valueOf() === b.valueOf();
		if (a.toString !== Object.prototype.toString) return a.toString() === b.toString();

		const keys = Object.keys(a);
		const length = keys.length;
		if (length !== Object.keys(b).length) return false;

		for (let i = length; i-- !== 0; ) {
			if (!Object.prototype.hasOwnProperty.call(b, keys[i])) return false;
		}

		for (let i = length; i-- !== 0; ) {
			const key = keys[i];

			if (!dataEqual(a[key], b[key])) {
				return false;
			}
		}

		return true;
	}

	// true if both NaN, false otherwise
	return a !== a && b !== b;
}
